import { Check } from "lucide-react";

const NEEDS = [
  "Deep contract and agreement analysis",
  "Strategic business risk assessments",
  "Complex issue investigation and fact-finding",
  "Independent review of critical decisions",
  "Clear recommendations grounded in evidence and experience",
  "A trusted advisor who understands both the legal and business sides of a problem",
];

export const AboutAngelica = () => {
  return (
    <section id="angelica" data-testid="angelica-section" className="relative py-24 lg:py-32 bg-navy">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Photo + sticky intro */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold mb-4">Founder</p>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight">
                About <span className="gold-text">Angelica</span>
              </h2>
              <div className="relative mt-8 max-w-sm">
                <div className="absolute -inset-3 border border-gold/30 translate-x-4 translate-y-4" />
                <img
                  data-testid="angelica-photo"
                  src="https://customer-assets.emergentagent.com/job_expert-services-17/artifacts/w08paz4y_Screenshot_20260630_121805_Photos.jpg"
                  alt="Angelica, Founder of Six12 Consulting"
                  className="relative w-full aspect-[4/5] object-cover object-top"
                />
              </div>
              <p className="mt-6 max-w-sm text-sm text-gold italic font-display">
                "Information creates insight, insight creates clarity, and clarity creates confidence."
              </p>
            </div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-7 space-y-5 text-slate-300 leading-relaxed">
            <p className="text-lg text-white">
              Complex problems rarely announce themselves as problems. They hide in contracts, operational
              blind spots, compliance gaps, business processes, and assumptions that no one has challenged.
              My job is to find them before they become expensive.
            </p>
            <p>
              With more than a decade of legal, investigative, and business advisory experience, I help
              organizations identify risk, uncover opportunity, and make informed decisions with confidence.
              My career has spanned government investigations, complex litigation, tax incentive consulting,
              contract analysis, and international team leadership—giving me a unique ability to see issues
              from multiple angles and connect details that others overlook.
            </p>
            <p>
              I began my career as a prosecutor, where I handled thousands of cases and developed the
              investigative instincts that still define my work today. Success depended on the ability to
              quickly analyze large volumes of information, identify critical facts, evaluate credibility,
              and make strategic decisions under pressure. Those same skills now help businesses navigate
              uncertainty, evaluate risk, and solve complex operational challenges.
            </p>
            <p>
              Later, as a Senior Complex Tax Attorney and international leader, I managed teams across
              multiple countries, advised clients on high-stakes financial matters, reviewed sophisticated
              commercial agreements, and helped generate hundreds of millions of dollars in client value
              through strategic analysis and problem-solving. I became known for seeing opportunities others
              missed and translating complex information into clear, actionable guidance.
            </p>
            <p>
              Today, through Six12 Consulting, I bring that same approach to every engagement. I am not a
              consultant who delivers generic reports filled with buzzwords. I am an investigator, strategist,
              and problem solver. I ask difficult questions. I challenge assumptions. I identify risks before
              they become liabilities and opportunities before they become obvious.
            </p>

            <div className="border border-white/10 bg-navy-800 p-8 my-8">
              <h3 className="font-display text-xl mb-6">Clients hire me when they need:</h3>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {NEEDS.map((n) => (
                  <li key={n} className="flex items-start gap-3 text-slate-300">
                    <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p>
              Whether I am reviewing a contract, evaluating a business process, investigating a concern, or
              helping a client navigate uncertainty, my goal is always the same—to provide the information and
              analysis necessary for confident decision-making.
            </p>
            <p className="font-display text-2xl text-white pt-4 border-l-2 border-gold pl-6">
              Because the most expensive mistakes are often the ones you never saw coming.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
